import { CoherencePagination, ICoherencePaginationProps } from "@coherence-design-system/controls";
import { mergeStyleSets } from "@fluentui/react";
import React, { useEffect, useRef, useState } from "react";

export const RECORDS_PER_PAGE_OPTIONS = [10, 25, 50, 100] as const;
export type RecordsPerPage = typeof RECORDS_PER_PAGE_OPTIONS[number];
export const DEFAULT_RECORDS_PER_PAGE: RecordsPerPage = 10;
const PAGE_SIZE_STORAGE_PREFIX = "opp:grid-page-size:";

export const recordsPerPageDropdownOptions: ReadonlyArray<{ key: RecordsPerPage; text: string }> =
  RECORDS_PER_PAGE_OPTIONS.map((pageSize) => ({ key: pageSize, text: pageSize.toString() }));

export const normalizeRecordsPerPage = (pageSize: number): RecordsPerPage =>
  RECORDS_PER_PAGE_OPTIONS.includes(pageSize as RecordsPerPage)
    ? pageSize as RecordsPerPage
    : DEFAULT_RECORDS_PER_PAGE;

export const getPersistedRecordsPerPage = (
  preferenceKey: string,
  fallbackPageSize: number = DEFAULT_RECORDS_PER_PAGE
): RecordsPerPage => {
  const storedPageSize = localStorage.getItem(`${PAGE_SIZE_STORAGE_PREFIX}${preferenceKey}`);
  return normalizeRecordsPerPage(
    storedPageSize === null ? fallbackPageSize : Number(storedPageSize)
  );
};

export const persistRecordsPerPage = (
  preferenceKey: string,
  pageSize: RecordsPerPage
): void => {
  localStorage.setItem(`${PAGE_SIZE_STORAGE_PREFIX}${preferenceKey}`, pageSize.toString());
};

const styles = mergeStyleSets({
  controls: {
    display: "flex",
    justifyContent: "center",
    marginTop: "8px"
  }
});

const paginationStyles: ICoherencePaginationProps["styles"] = {
  root: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "52px"
  },
  paginationContainer: {
    gridColumn: "auto",
    padding: "0 4px 0 0"
  },
  pageSizeContainer: {
    gridColumn: "auto",
    justifyContent: "flex-start",
    marginTop: 0
  }
};

interface PaginationControlsProps {
  totalRecords: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: RecordsPerPage) => void;
}

export const PaginationControls: React.FunctionComponent<PaginationControlsProps> = ({
  totalRecords,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange
}) => {
  const forwardedPageSizeRef = useRef<RecordsPerPage | null>(null);

  if (totalRecords <= 0) return null;

  const normalizedPageSize = normalizeRecordsPerPage(pageSize);
  const pageCount = Math.max(1, Math.ceil(totalRecords / normalizedPageSize));
  const selectedPage = Math.min(Math.max(currentPage, 1), pageCount);
  const handlePageChange = (
    startItemIndex: number,
    endItemIndex: number,
    newSelectedPage?: number
  ): void => {
    const selectedPageNumber = newSelectedPage ?? 1;
    const nextPageSize = RECORDS_PER_PAGE_OPTIONS.includes(endItemIndex as RecordsPerPage)
      ? endItemIndex as RecordsPerPage
      : null;

    // Coherence emits this page callback immediately before its page-size callback.
    if (
      startItemIndex === 0 &&
      selectedPageNumber === 1 &&
      nextPageSize !== null &&
      nextPageSize !== normalizedPageSize
    ) {
      forwardedPageSizeRef.current = nextPageSize;
      onPageSizeChange(nextPageSize);
      return;
    }

    onPageChange(selectedPageNumber);
  };
  const handlePageSizeChange = (nextPageSize: number): void => {
    const normalizedNextPageSize = normalizeRecordsPerPage(nextPageSize);
    if (forwardedPageSizeRef.current === normalizedNextPageSize) {
      forwardedPageSizeRef.current = null;
      return;
    }
    if (normalizedNextPageSize !== normalizedPageSize) {
      onPageSizeChange(normalizedNextPageSize);
    }
  };
  const paginationProps: ICoherencePaginationProps = {
    pageCount,
    pageSize: {
      pageSize: normalizedPageSize,
      pageSizeList: [...recordsPerPageDropdownOptions],
      onPageSizeChange: handlePageSizeChange,
      comboBoxWidth: 72,
      comboBoxAriaLabel: "Records per page",
      initialPageSizeLabelText: "",
      endingPageSizeLabelText: ""
    },
    selectedPage,
    previousPageAriaLabel: "previous",
    nextPageAriaLabel: "next",
    inputFieldAriaLabel: "page number",
    onPageChange: handlePageChange,
    canJumpToPage: true,
    styles: paginationStyles
  };

  return (
    <div className={styles.controls}>
      <CoherencePagination {...paginationProps} />
    </div>
  );
};

interface CustomPaginationProps<T> {
  data: readonly T[];
  updatePaginationData: (data: T[]) => void;
  refreshPagination: unknown;
  preferenceKey: string;
  pageSize?: number;
  onPageSizeChange?: (pageSize: RecordsPerPage) => void;
}

const CustomPagination = <T,>({
  data,
  updatePaginationData,
  refreshPagination,
  preferenceKey,
  pageSize = DEFAULT_RECORDS_PER_PAGE,
  onPageSizeChange
}: CustomPaginationProps<T>): React.ReactElement | null => {
  const normalizedPageSizeProp = normalizeRecordsPerPage(pageSize);
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [selectedPageSize, setSelectedPageSize] = useState<RecordsPerPage>(
    () => getPersistedRecordsPerPage(preferenceKey, normalizedPageSizeProp)
  );
  const updatePaginationDataRef = useRef(updatePaginationData);
  const previousRefreshRef = useRef(refreshPagination);
  const previousPageSizePropRef = useRef(normalizedPageSizeProp);
  const skipNextPaginationEffectRef = useRef(false);

  useEffect(() => {
    updatePaginationDataRef.current = updatePaginationData;
  }, [updatePaginationData]);

  useEffect(() => {
    if (previousPageSizePropRef.current === normalizedPageSizeProp) return;

    previousPageSizePropRef.current = normalizedPageSizeProp;
    setSelectedPage(1);
    setSelectedPageSize(normalizedPageSizeProp);
    persistRecordsPerPage(preferenceKey, normalizedPageSizeProp);
  }, [normalizedPageSizeProp, preferenceKey]);

  useEffect(() => {
    if (skipNextPaginationEffectRef.current) {
      skipNextPaginationEffectRef.current = false;
      return;
    }

    const refreshChanged = !Object.is(previousRefreshRef.current, refreshPagination);
    previousRefreshRef.current = refreshPagination;
    const requestedPage = refreshChanged ? 1 : selectedPage;
    const pageCount = Math.max(1, Math.ceil(data.length / selectedPageSize));
    const clampedPage = Math.min(Math.max(requestedPage, 1), pageCount);
    if (clampedPage !== selectedPage) {
      skipNextPaginationEffectRef.current = true;
      setSelectedPage(clampedPage);
    }

    const firstRecordIndex = (clampedPage - 1) * selectedPageSize;
    updatePaginationDataRef.current(data.slice(firstRecordIndex, firstRecordIndex + selectedPageSize));
  }, [data, refreshPagination, selectedPage, selectedPageSize]);

  const handlePageSizeChange = (nextPageSize: RecordsPerPage): void => {
    setSelectedPage(1);
    setSelectedPageSize(nextPageSize);
    persistRecordsPerPage(preferenceKey, nextPageSize);
    onPageSizeChange?.(nextPageSize);
  };

  return data.length > 0 ? (
    <PaginationControls
      totalRecords={data.length}
      currentPage={selectedPage}
      pageSize={selectedPageSize}
      onPageChange={setSelectedPage}
      onPageSizeChange={handlePageSizeChange}
    />
  ) : null;
};

export default CustomPagination;